/* What the "Fix SQL" panel says about each statement, and the file it downloads.
 *
 * **The panel never decides what a statement does — the server's `purpose` and `needs_value` do.** A
 * screen that inferred "this one is safe to run" from the SQL text would be a second answer to a
 * question the platform already answered when it rendered the statement, and the one place a wrong
 * answer costs somebody's data. So every label here is keyed on those two fields and nothing else.
 */

import type {
  FixKind,
  ResolutionStatement,
  RuleKind,
  ViolationResolution,
} from '../api/rules';

/* A review statement means a different thing per kind, so its title does too: for duplicates it is
 * the copies side by side, for an orphan the parent keys that are missing, for a table-level finding
 * the figure that confirms it. A total map, so a new kind is a compile error here rather than a
 * statement titled with another kind's sentence. */
const REVIEW_TITLE = {
  not_null: 'resolution.statement.review.generic',
  unique: 'resolution.statement.review.unique',
  accepted_values: 'resolution.statement.review.generic',
  range: 'resolution.statement.review.generic',
  pattern: 'resolution.statement.review.generic',
  freshness: 'resolution.statement.review.freshness',
  row_count_anomaly: 'resolution.statement.review.row_count_anomaly',
  referential_integrity: 'resolution.statement.review.referential_integrity',
  custom_sql: 'resolution.statement.review.generic',
} satisfies Record<RuleKind, string>;

export function statementTitleKey(
  statement: ResolutionStatement,
  kind: RuleKind,
): string {
  if (statement.purpose === 'find') return 'resolution.statement.find';
  if (statement.purpose === 'review') return REVIEW_TITLE[kind];
  return statement.needs_value
    ? 'resolution.statement.fix.template'
    : 'resolution.statement.fix.exact';
}

/* Advice that belongs to one kind's fix rather than to every fix. Freshness is the case worth a
 * sentence: its template only makes the rule pass if the value written is recent, and when the data
 * really is old the honest fix is reloading the table, not changing a date. */
export const KIND_NOTE: Partial<Record<RuleKind, string>> = {
  freshness: 'resolution.kindNote.freshness',
  row_count_anomaly: 'resolution.kindNote.row_count_anomaly',
};

export const FIX_KIND_KEY = {
  exact: 'resolution.fixKind.exact',
  template: 'resolution.fixKind.template',
  none: 'resolution.fixKind.none',
} satisfies Record<FixKind, string>;

/** The downloaded script: every statement in run order, each under a comment naming what it does.
 *
 * Titles arrive already translated so this stays a pure function a test can hold. A template keeps
 * its `REPLACE` placeholder in the file, deliberately — a script that ran top to bottom must stop at
 * the first value nobody chose rather than write one. */
export function resolutionToSql(
  resolution: ViolationResolution,
  banner: string,
  titleOf: (statement: ResolutionStatement) => string,
): string {
  const header = [
    `-- ${banner}`,
    `-- ${resolution.rule_name} (${resolution.rule_kind}) on ${resolution.relation}`,
    `-- ${resolution.engine}${resolution.connection_name === null ? '' : ` · ${resolution.connection_name}`}`,
  ];
  const body = resolution.statements.map(
    (statement, index) =>
      `-- ${String(index + 1)}. ${titleOf(statement)}\n${statement.sql};`,
  );
  return `${header.join('\n')}\n\n${body.join('\n\n')}\n`;
}
